import { CheckCircle2, Info, X, XCircle } from 'lucide-react'
import {
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react'

import { Button } from '@/components/ui/button'
import type { InterfaceErrorDiagnostics } from '@shared/i18n/interface-messages'

type ToastTone = 'success' | 'error' | 'info'
type ToastInput = { title: string; description?: string; tone?: ToastTone; diagnostics?: InterfaceErrorDiagnostics }
type ToastItem = ToastInput & { id: number; tone: ToastTone }

type ToastContextValue = {
	notify: (toast: ToastInput) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export const ToastProvider = ({ children }: PropsWithChildren) => {
	const [items, setItems] = useState<ToastItem[]>([])
	const sequence = useRef(0)
	const dismiss = useCallback((id: number) => {
		setItems(current => current.filter(item => item.id !== id))
	}, [])
	const notify = useCallback(
		(input: ToastInput) => {
			const item: ToastItem = {
				...input,
				id: ++sequence.current,
				tone: input.tone ?? 'info',
			}
			setItems(current => [...current, item].slice(-4))
			window.setTimeout(() => dismiss(item.id), 5000)
		},
		[dismiss],
	)
	const value = useMemo(() => ({ notify }), [notify])
	return (
		<ToastContext.Provider value={value}>
			{children}
			<div
				className='pointer-events-none fixed inset-x-4 bottom-4 z-[100] flex flex-col items-end gap-2 sm:left-auto sm:w-96'
				aria-live='polite'
				aria-relevant='additions'>
				{items.map(item => {
					const Icon = item.tone === 'success' ? CheckCircle2 : item.tone === 'error' ? XCircle : Info
					return (
						<div
							key={item.id}
							className='pointer-events-auto flex w-full gap-3 rounded-xl border bg-white p-4 shadow-lg'>
							<Icon
								className={
									item.tone === 'success'
										? 'mt-0.5 h-5 w-5 text-emerald-600'
										: item.tone === 'error'
											? 'mt-0.5 h-5 w-5 text-red-600'
											: 'mt-0.5 h-5 w-5 text-blue-600'
								}
							/>
							<div className='min-w-0 flex-1'>
								<p className='text-sm font-semibold'>{item.title}</p>
								{item.description ? (
									<p className='mt-1 text-sm text-muted-foreground'>{item.description}</p>
								) : null}
								{item.diagnostics ? (
									<details className='mt-2 rounded-lg border px-3 py-2 text-left text-xs text-muted-foreground'>
										<summary className='cursor-pointer font-medium'>Технические сведения</summary>
										<div className='mt-2 space-y-1 font-mono'>
											{item.diagnostics.code ? <p>Код ошибки: {item.diagnostics.code}</p> : null}
											{item.diagnostics.requestId ? <p>Идентификатор запроса: {item.diagnostics.requestId}</p> : null}
											{item.diagnostics.technicalMessage ? <p>{item.diagnostics.technicalMessage}</p> : null}
										</div>
									</details>
								) : null}
							</div>
							<Button
								variant='ghost'
								size='icon'
								className='h-7 w-7'
								onClick={() => dismiss(item.id)}
								aria-label='Закрыть уведомление'>
								<X className='h-4 w-4' />
							</Button>
						</div>
					)
				})}
			</div>
		</ToastContext.Provider>
	)
}

export const useToast = () => {
	const value = useContext(ToastContext)
	if (!value) throw new Error('useToast must be used inside ToastProvider')
	return value
}
